import React, { useRef, useState, useMemo } from "react";
import ReactToPrint from "react-to-print";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSelector } from "react-redux";
import { updateAiSummary } from "../../connections/putApi";
import { showToast } from "../../utils/toastUtil";
import { primaryBtn } from "../../style/AppointmentStyle";
import logo from "../../assets/logo_png.png";

const humanize = (str) =>
  str
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

function getVasColor(score) {
  if (score <= 3) return "#16a34a";
  if (score <= 6) return "#d97706";
  return "#dc2626";
}

function getVasLabel(score) {
  if (score <= 3) return "Mild";
  if (score <= 6) return "Moderate";
  return "Severe";
}

function extractVasScore(summary) {
  const text =
    summary?.clinicalStatus?.painSummary ||
    summary?.clinicalStatus?.globalHealth ||
    "";
  const m = text.match(/(\d+)\s*\/\s*10/);
  return m ? parseInt(m[1]) : null;
}

// ─── Wellbeing Helpers ────────────────────────────────────────────────────────

const PERMA_DIMS = [
  {
    key: "P",
    label: "Positive",
    color: "#ef4444",
    match: /joy|peace|happy|positive/i,
  },
  { key: "E", label: "Engage", color: "#f59e0b", match: /engag/i },
  { key: "R", label: "Relat.", color: "#22c55e", match: /relat|social/i },
  { key: "M", label: "Meaning", color: "#3b82f6", match: /meaning|purpose/i },
  {
    key: "A",
    label: "Accomp.",
    color: "#8b5cf6",
    match: /confid|accompl|achiev/i,
  },
  {
    key: "H",
    label: "Health",
    color: "#14b8a6",
    match: /health|circad|sleep|physical/i,
  },
];

function parseWellbeingScores(text) {
  if (!text) return { dims: [], mean: null };
  const scoreMap = {};
  for (const [, name, score] of text.matchAll(
    /([A-Za-z][A-Za-z\s]+?):\s*(\d+(?:\.\d+)?)\s*\/\s*10/g,
  )) {
    const trimmed = name.trim();
    if (/^mean/i.test(trimmed)) continue;
    scoreMap[trimmed] = parseFloat(score);
  }
  const dims = [];
  for (const dim of PERMA_DIMS) {
    for (const [name, score] of Object.entries(scoreMap)) {
      if (dim.match.test(name)) {
        dims.push({ ...dim, score });
        break;
      }
    }
  }
  const meanMatch = text.match(/mean:\s*(\d+(?:\.\d+)?)\s*\/\s*10/i);
  return { dims, mean: meanMatch ? parseFloat(meanMatch[1]) : null };
}

function extractSpecialists(referrals) {
  if (!referrals?.length) return [];
  return referrals
    .map((ref) => {
      const m = ref.match(/our\s+([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)?)/);
      return m ? m[1] : ref.split(/\s+/).slice(0, 3).join(" ");
    })
    .filter(Boolean);
}

// ─── Dynamic state helpers ────────────────────────────────────────────────────

// Sections that need special UI treatment — keeps known layout
const SPECIAL_BANNER = "diagnosisTrack";
const SPECIAL_CHIEF = "chiefComplaints";
const GRID_SECTIONS = new Set(["clinicalStatus", "holisticDashboard"]);
const ARRAY_TEXT_KEYS = new Set(["redFlagsDetected", "alliedReferrals"]);
const RED_KEYS = new Set(["redFlagsDetected"]);
const SKIP_UI = new Set(["fullTranscript"]);
const ALL_KNOWN = new Set([
  "diagnosisTrack",
  "chiefComplaints",
  "clinicalStatus",
  "holisticDashboard",
  "clinicalImpression",
  "redFlagsDetected",
  "alliedReferrals",
  "fullTranscript",
]);

// Convert summary → flat editable state (arrays of strings → newline text)
function initEdited(summary) {
  if (!summary) return {};
  return Object.fromEntries(
    Object.entries(summary).map(([k, v]) => {
      if (Array.isArray(v) && v.every((x) => typeof x === "string"))
        return [k, v.join("\n")];
      if (typeof v === "object" && v !== null && !Array.isArray(v))
        return [k, { ...v }];
      return [k, v ?? ""];
    }),
  );
}

// Convert edited state → original shape for save / print
function toSummaryShape(edited, originalSummary) {
  return Object.fromEntries(
    Object.entries(edited).map(([k, v]) => {
      const orig = originalSummary?.[k];
      if (typeof v === "string" && Array.isArray(orig))
        return [k, v.split("\n").filter(Boolean)];
      return [k, v];
    }),
  );
}

// ─── Print Layout ─────────────────────────────────────────────────────────────

const PrintContent = React.forwardRef(
  ({ summary, patientDetails, appointmentData }, ref) => {
    const vasScore = extractVasScore(summary);
    const visitDate = appointmentData?.visit_date
      ? new Date(appointmentData.visit_date).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : new Date().toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });

    const clinicalStatus =
      summary?.clinicalStatus || summary?.categorySummary || {};
    const holistic = summary?.holisticDashboard || {};
    const holisticDisplay = Object.fromEntries(
      Object.entries(holistic).filter(
        ([k, v]) =>
          k !== "wellbeingSnapshot" &&
          v &&
          v !== "Not assessed" &&
          v !== "Not reported",
      ),
    );
    const hasHolistic = Object.keys(holisticDisplay).length > 0;
    const { dims: wbDims, mean: wbMean } = parseWellbeingScores(
      summary?.holisticDashboard?.wellbeingSnapshot,
    );

    return (
      <div ref={ref}>
        <style>{`
          * { margin: 0; padding: 0; box-sizing: border-box; }
          @page { margin: 10mm; }
          .rpt { font-family: Arial, sans-serif; font-size: 12px; color: #0f172a; line-height: 1.5; }
          .rpt-header { background: linear-gradient(135deg, #f0f9f4 0%, #fef9f0 100%); padding: 14px 20px 0; border: 1px solid #e2e8f0; border-radius: 8px; margin-bottom: 14px; }
          .rpt-header-top { display: flex; justify-content: space-between; align-items: center; }
          .rpt-badge { display: inline-flex; align-items: center; gap: 5px; background: linear-gradient(135deg, #0097B2, #007A99); color: white; font-size: 10px; font-weight: 600; letter-spacing: 0.5px; padding: 4px 12px; border-radius: 20px; }
          .rpt-patient-bar { padding: 8px 0 14px; display: flex; justify-content: space-between; align-items: center; }
          .rpt-name { font-size: 13px; font-weight: 700; }
          .rpt-uid { font-size: 11px; color: #64748b; margin-left: 8px; font-weight: 500; }
          .rpt-date { font-size: 11px; color: #64748b; font-weight: 500; }
          .rpt-vas { display: flex; align-items: center; gap: 12px; padding: 10px 16px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; margin-bottom: 14px; }
          .rpt-vas-lbl { font-size: 11px; font-weight: 600; color: #475569; }
          .rpt-vas-score { font-size: 22px; font-weight: 700; }
          .rpt-vas-pill { font-size: 10px; font-weight: 600; padding: 2px 8px; border-radius: 10px; color: white; }
          .rpt-chief { background: rgba(0,151,178,0.05); border: 1px solid rgba(0,151,178,0.2); border-radius: 6px; padding: 10px 14px; margin-bottom: 14px; font-size: 13px; font-weight: 500; }
          .rpt-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }
          .rpt-grid-single { grid-template-columns: 1fr; }
          .rpt-card { border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
          .rpt-card-title { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #0097B2; padding: 8px 14px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; }
          .rpt-field { display: flex; padding: 6px 14px; border-bottom: 1px solid #f1f5f9; font-size: 11px; }
          .rpt-field:last-child { border-bottom: none; }
          .rpt-field-name { width: 110px; min-width: 110px; font-weight: 600; color: #475569; padding-top: 1px; }
          .rpt-field-val { color: #1e293b; line-height: 1.45; flex: 1; }
          .rpt-section-title { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #0097B2; margin-bottom: 6px; }
          .rpt-impression { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px 14px; margin-bottom: 14px; font-size: 11px; color: #1e293b; line-height: 1.6; }
          .rpt-disclaimer { margin-top: 10px; text-align: center; font-size: 8px; color: #94a3b8; }
          .rpt-footer { margin-top: 8px; padding-top: 8px; text-align: center; border-top: 1px solid #e2e8f0; font-size: 10px; color: #64748b; font-style: italic; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        `}</style>

        <div className="rpt">
          {/* ── Header ── */}
          <div className="rpt-header">
            <div className="rpt-header-top">
              <div style={{ fontWeight: 700, fontSize: 15, color: "#0097B2" }}>
                {/* Patient AI Summary */}
                <img src={logo} alt="logo" height="26px" />
              </div>

              <span className="rpt-badge">
                <span style={{ fontSize: 15 }}>✦</span> AI Powered
              </span>
            </div>
            <div className="rpt-patient-bar">
              <div>
                <span className="rpt-name">
                  {patientDetails?.full_name?.toUpperCase()}
                </span>
                <span className="rpt-uid">({patientDetails?.uid})</span>
              </div>
              <div className="rpt-date">{visitDate}</div>
            </div>
          </div>

          {/* ── VAS ── */}
          {vasScore !== null && (
            <div className="rpt-vas">
              <span className="rpt-vas-lbl">VAS Pain Score</span>
              <span
                className="rpt-vas-score"
                style={{ color: getVasColor(vasScore) }}
              >
                {vasScore}/10
              </span>
              <span
                className="rpt-vas-pill"
                style={{ background: getVasColor(vasScore) }}
              >
                {getVasLabel(vasScore)}
              </span>
            </div>
          )}

          {/* ── Chief Complaints ── */}
          {summary?.chiefComplaints && (
            <div className="rpt-chief">{summary.chiefComplaints}</div>
          )}

          {/* ── Clinical Status + Holistic Grid ── */}
          <div className={`rpt-grid ${hasHolistic ? "" : "rpt-grid-single"}`}>
            {Object.keys(clinicalStatus).length > 0 && (
              <div className="rpt-card">
                <div className="rpt-card-title">Clinical Status</div>
                {Object.entries(clinicalStatus).map(([key, value]) =>
                  value && typeof value === "string" ? (
                    <div className="rpt-field" key={key}>
                      <div className="rpt-field-name">{humanize(key)}</div>
                      <div className="rpt-field-val">{value}</div>
                    </div>
                  ) : null,
                )}
              </div>
            )}
            {hasHolistic && (
              <div className="rpt-card">
                <div className="rpt-card-title">Lifestyle &amp; Wellbeing</div>
                {Object.entries(holisticDisplay).map(([key, value]) =>
                  value && typeof value === "string" ? (
                    <div className="rpt-field" key={key}>
                      <div className="rpt-field-name">{humanize(key)}</div>
                      <div className="rpt-field-val">{value}</div>
                    </div>
                  ) : null,
                )}
              </div>
            )}
          </div>

          {/* ── Wellbeing Profile chart ── */}
          {wbDims.length > 0 && (
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                padding: "14px",
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  color: "#0097B2",
                  marginBottom: 10,
                }}
              >
                <span>Wellbeing Profile</span>
                {wbMean !== null && (
                  <span
                    style={{
                      fontWeight: 500,
                      textTransform: "none",
                      letterSpacing: 0,
                      color: "#64748b",
                    }}
                  >
                    Mean: {wbMean}/10
                  </span>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  gap: 10,
                  height: 100,
                }}
              >
                {wbDims.map(({ key, label, color, score }) => (
                  <div
                    key={key}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      flex: 1,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color,
                        marginBottom: 2,
                      }}
                    >
                      {score}
                    </span>
                    <div
                      style={{
                        width: "100%",
                        maxWidth: 36,
                        height: score * 9,
                        background: color,
                        borderRadius: "3px 3px 0 0",
                        minHeight: 2,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color,
                        marginTop: 4,
                      }}
                    >
                      {key}
                    </span>
                    <span
                      style={{
                        fontSize: 7,
                        color: "#94a3b8",
                        textAlign: "center",
                      }}
                    >
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rpt-disclaimer">
            This automated summary is for reference only. Clinical judgment
            should always take precedence.
          </div>
          <div className="rpt-footer">
            Intelligently Summarized by Doctat • www.doctat.com
          </div>
        </div>
      </div>
    );
  },
);

PrintContent.displayName = "PrintContent";

// ─── Shared UI components ─────────────────────────────────────────────────────

const FieldRow = ({ label, value, onChange, multiline = false }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
    <Typography
      sx={{
        width: 130,
        minWidth: 130,
        fontSize: 11,
        fontWeight: 600,
        color: "#475569",
        pt: multiline ? 0.8 : 1.1,
        lineHeight: 1.4,
      }}
    >
      {humanize(label)}
    </Typography>
    <TextField
      fullWidth
      size="small"
      multiline={multiline}
      minRows={multiline ? 2 : 1}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      variant="outlined"
      sx={{
        "& .MuiOutlinedInput-root": {
          fontSize: 11,
          borderRadius: 1,
          "& fieldset": { borderColor: "#e2e8f0" },
          "&:hover fieldset": { borderColor: "#0097B2" },
        },
      }}
    />
  </Box>
);

const SectionCard = ({ title, children, empty = false }) => {
  if (empty) return null;
  return (
    <Box
      sx={{
        border: "1px solid #e2e8f0",
        borderRadius: 2,
        overflow: "hidden",
        mb: 2,
      }}
    >
      <Box
        sx={{
          background: "#f8fafc",
          borderBottom: "1px solid #e2e8f0",
          px: 2,
          py: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 1,
            color: "#0097B2",
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: 1.5 }}>{children}</Box>
    </Box>
  );
};

const SectionLabel = ({ children, color = "#0097B2" }) => (
  <Typography
    sx={{
      fontSize: 12,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 1,
      color,
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

const WellbeingProfile = ({ wellbeingSnapshot }) => {
  const { dims, mean } = parseWellbeingScores(wellbeingSnapshot);
  if (!dims.length) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <SectionLabel>Wellbeing Profile</SectionLabel>
        {mean !== null && (
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#475569" }}>
            Mean:{" "}
            <span style={{ color: "#0f172a", fontWeight: 700 }}>{mean}/10</span>
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          border: "1px solid #e2e8f0",
          borderRadius: 2,
          px: 2,
          pt: 1.5,
          pb: 0.5,
          background: "#f8fafc",
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "flex-end", gap: 2, height: 100 }}
        >
          {dims.map(({ key, color, score }) => (
            <Box
              key={key}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                flex: 1,
              }}
            >
              <Typography
                sx={{ fontSize: 12, fontWeight: 700, color, mb: 0.3 }}
              >
                {score}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 40,
                  height: score * 10,
                  background: color,
                  borderRadius: "3px 3px 0 0",
                  minHeight: 3,
                }}
              />
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
          {dims.map(({ key, label, color }) => (
            <Box
              key={key}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                flex: 1,
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 700, color }}>
                {key}
              </Typography>
              <Typography
                sx={{
                  fontSize: 8,
                  color: "#64748b",
                  textAlign: "center",
                  lineHeight: 1.2,
                }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const SuggestedReferrals = ({ alliedReferrals }) => {
  const specialists = extractSpecialists(alliedReferrals);
  if (!specialists.length) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <SectionLabel>Suggested Referrals</SectionLabel>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {specialists.map((s, i) => (
          <Chip
            key={i}
            label={s}
            variant="outlined"
            size="small"
            sx={{
              borderColor: "#0097B2",
              color: "#0097B2",
              fontWeight: 600,
              fontSize: 11,
              borderRadius: 1.5,
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

// ─── Generic renderers for unknown API keys ───────────────────────────────────

const GenericStringSection = ({ fieldKey, value, onChange }) => (
  <Box sx={{ mb: 2 }}>
    <SectionLabel>{humanize(fieldKey)}</SectionLabel>
    <TextField
      fullWidth
      multiline
      minRows={2}
      size="small"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      variant="outlined"
      sx={{
        "& .MuiOutlinedInput-root": {
          fontSize: 12,
          "& fieldset": { borderColor: "#e2e8f0" },
        },
      }}
    />
  </Box>
);

const GenericObjectSection = ({ fieldKey, value, onFieldChange }) => {
  const entries = Object.entries(value || {}).filter(
    ([, v]) => typeof v === "string",
  );
  if (!entries.length) return null;
  return (
    <SectionCard title={humanize(fieldKey)}>
      {entries.map(([k, v]) => (
        <FieldRow
          key={k}
          label={k}
          value={v}
          onChange={(val) => onFieldChange(k, val)}
          multiline
        />
      ))}
    </SectionCard>
  );
};

const GenericArraySection = ({ fieldKey, value, onChange }) => (
  <Box sx={{ mb: 2 }}>
    <SectionLabel>{humanize(fieldKey)}</SectionLabel>
    <TextField
      fullWidth
      multiline
      minRows={2}
      size="small"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder="One per line..."
      variant="outlined"
      sx={{
        "& .MuiOutlinedInput-root": {
          fontSize: 12,
          "& fieldset": { borderColor: "#e2e8f0" },
        },
      }}
    />
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const PatientHistorySummary = ({ summary, onClose, onContinue }) => {
  const printRef = useRef();
  const patientDetails = useSelector(
    (state) => state.prescription.patientDetailsData,
  );
  const appointmentData = useSelector(
    (state) => state.prescription.appointmentData,
  );

  // ── Dynamic state: handles any keys returned by the API ──
  const [edited, setEdited] = useState(() => initEdited(summary));
  const [saving, setSaving] = useState(false);

  const set = (path, value) => {
    setEdited((prev) => {
      if (path.includes(".")) {
        const dotIdx = path.indexOf(".");
        const section = path.slice(0, dotIdx);
        const field = path.slice(dotIdx + 1);
        return { ...prev, [section]: { ...prev[section], [field]: value } };
      }
      return { ...prev, [path]: value };
    });
  };

  // Fully-shaped summary for print + save (arrays restored)
  const summaryForPrint = useMemo(
    () => toSummaryShape(edited, summary),
    [edited, summary],
  );

  const handleSaveAndContinue = async () => {
    setSaving(true);
    try {
      const payload = {
        summary_type: "octabot",
        resource_id: appointmentData?.id,
        resource_type: "Appointment",
        ...summaryForPrint,
      };
      const res = await updateAiSummary(payload);
      if (res?.status?.status === "SUCCESS") {
        showToast("Summary saved successfully", "SUCCESS");
        onContinue?.(summaryForPrint);
      } else {
        showToast(res?.status?.message || "Failed to save summary", "ERROR");
      }
    } catch (error) {
      console.error(error);
      showToast("Failed to save summary", "ERROR");
    } finally {
      setSaving(false);
    }
  };

  const vasScore = extractVasScore({ clinicalStatus: edited.clinicalStatus });

  // Keys not in the known set → rendered generically after known sections
  const extraKeys = Object.keys(edited).filter((k) => !ALL_KNOWN.has(k));

  // Determine if clinicalStatus / holisticDashboard have editable string fields
  const clinicalStatusEntries = Object.entries(
    edited.clinicalStatus || {},
  ).filter(([, v]) => typeof v === "string");
  const holisticEntries = Object.entries(edited.holisticDashboard || {}).filter(
    ([k, v]) => k !== "wellbeingSnapshot" && typeof v === "string",
  );

  return (
    <Box
      sx={{
        width: { xs: "95vw", md: "85vw" },
        maxHeight: "92vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6" fontWeight={700} color="#0f172a">
            AI Patient Summary
          </Typography>
          <Chip
            label="✦ AI Powered"
            size="small"
            className="rpt-badge"
            sx={{
              // background: "linear-gradient(135deg, #0097B2  #0097B2)",
              color: "white",
              fontWeight: 600,
              fontSize: 12,
            }}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ReactToPrint
            trigger={() => (
              <Button
                startIcon={<PrintIcon />}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: "#0097B2",
                  color: "#0097B2",
                  borderRadius: 2,
                }}
              >
                Print
              </Button>
            )}
            content={() => printRef.current}
          />
          <IconButton onClick={onClose} size="small">
            <CloseIcon sx={{ color: "red" }} />
          </IconButton>
        </Box>
      </Box>

      <Divider sx={{ mb: 2, flexShrink: 0 }} />

      {/* ── Scrollable Body ── */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          pr: 0.5,
          scrollbarWidth: "thin",
          scrollbarColor: "#e2e8f0 transparent",
        }}
      >
        {/* Diagnosis Track */}
        {edited.hasOwnProperty("diagnosisTrack") && (
          <Box
            sx={{
              background: "linear-gradient(135deg, #f0f9f4 0%, #fef9f0 100%)",
              border: "1px solid #e2e8f0",
              borderRadius: 2,
              p: 1.5,
              mb: 2,
            }}
          >
            <Typography
              sx={{ fontSize: 11, fontWeight: 600, color: "#475569", mb: 0.5 }}
            >
              Diagnosis Track
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={edited.diagnosisTrack}
              onChange={(e) => set("diagnosisTrack", e.target.value)}
              variant="standard"
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#0f172a",
                },
              }}
            />
          </Box>
        )}

        {/* VAS Score */}
        {vasScore !== null && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 1.5,
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 1.5,
              mb: 2,
            }}
          >
            <Typography
              sx={{ fontSize: 11, fontWeight: 600, color: "#475569" }}
            >
              VAS Pain Score
            </Typography>
            <Typography
              sx={{
                fontSize: 26,
                fontWeight: 700,
                color: getVasColor(vasScore),
              }}
            >
              {vasScore}/10
            </Typography>
            <Chip
              label={getVasLabel(vasScore)}
              size="small"
              sx={{
                background: getVasColor(vasScore),
                color: "white",
                fontWeight: 600,
                fontSize: 12,
              }}
            />
          </Box>
        )}

        {/* Chief Complaints */}
        {edited.hasOwnProperty("chiefComplaints") && (
          <Box
            sx={{
              background: "rgba(0,151,178,0.05)",
              border: "1px solid rgba(0,151,178,0.2)",
              borderRadius: 1.5,
              p: 1.5,
              mb: 2,
            }}
          >
            <Typography
              sx={{ fontSize: 11, fontWeight: 600, color: "#007A99", mb: 0.5 }}
            >
              Chief Complaints
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={edited.chiefComplaints}
              onChange={(e) => set("chiefComplaints", e.target.value)}
              variant="standard"
              sx={{
                "& .MuiInputBase-input": { fontSize: 13, fontWeight: 500 },
              }}
            />
          </Box>
        )}

        {/* Clinical Status + Holistic Grid */}
        {(clinicalStatusEntries.length > 0 || holisticEntries.length > 0) && (
          <Grid container spacing={2} sx={{ mb: 0 }}>
            {clinicalStatusEntries.length > 0 && (
              <Grid item xs={12} md={holisticEntries.length > 0 ? 6 : 12}>
                <SectionCard title="Clinical Status">
                  {clinicalStatusEntries.map(([key]) => (
                    <FieldRow
                      key={key}
                      label={key}
                      value={edited.clinicalStatus[key]}
                      onChange={(v) => set(`clinicalStatus.${key}`, v)}
                      multiline
                    />
                  ))}
                </SectionCard>
              </Grid>
            )}
            {holisticEntries.length > 0 && (
              <Grid item xs={12} md={clinicalStatusEntries.length > 0 ? 6 : 12}>
                <SectionCard title="Lifestyle & Wellbeing">
                  {holisticEntries.map(([key]) => (
                    <FieldRow
                      key={key}
                      label={key}
                      value={edited.holisticDashboard[key]}
                      onChange={(v) => set(`holisticDashboard.${key}`, v)}
                      multiline
                    />
                  ))}
                </SectionCard>
              </Grid>
            )}
          </Grid>
        )}

        {/* Clinical Impression */}
        {edited.hasOwnProperty("clinicalImpression") && (
          <Box sx={{ mb: 2 }}>
            <SectionLabel>Clinical Impression</SectionLabel>
            <TextField
              fullWidth
              multiline
              minRows={4}
              size="small"
              value={edited.clinicalImpression}
              onChange={(e) => set("clinicalImpression", e.target.value)}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: 12,
                  "& fieldset": { borderColor: "#e2e8f0" },
                },
              }}
            />
          </Box>
        )}

        {/* Red Flags */}
        {edited.hasOwnProperty("redFlagsDetected") && (
          <Box sx={{ mb: 2 }}>
            <SectionLabel color="#dc2626">⚠ Red Flags</SectionLabel>
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              value={edited.redFlagsDetected}
              onChange={(e) => set("redFlagsDetected", e.target.value)}
              placeholder="One per line..."
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: 12,
                  background: "#fef2f2",
                  "& fieldset": { borderColor: "#fecaca" },
                  "&:hover fieldset": { borderColor: "#dc2626" },
                },
              }}
            />
          </Box>
        )}

        {/* Allied Referrals */}
        {edited.hasOwnProperty("alliedReferrals") && (
          <Box sx={{ mb: 2 }}>
            <SectionLabel>Allied Referrals</SectionLabel>
            <TextField
              fullWidth
              multiline
              minRows={3}
              size="small"
              value={edited.alliedReferrals}
              onChange={(e) => set("alliedReferrals", e.target.value)}
              placeholder="One per line..."
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: 12,
                  "& fieldset": { borderColor: "#e2e8f0" },
                },
              }}
            />
          </Box>
        )}

        {/* ── Unknown / extra keys from API — rendered generically ── */}
        {extraKeys.map((key) => {
          const value = edited[key];
          if (SKIP_UI.has(key)) return null;

          // Nested object section
          if (
            typeof value === "object" &&
            !Array.isArray(value) &&
            value !== null
          ) {
            return (
              <GenericObjectSection
                key={key}
                fieldKey={key}
                value={value}
                onFieldChange={(field, val) => set(`${key}.${field}`, val)}
              />
            );
          }
          // Array stored as newline text
          if (typeof value === "string" && Array.isArray(summary?.[key])) {
            return (
              <GenericArraySection
                key={key}
                fieldKey={key}
                value={value}
                onChange={(v) => set(key, v)}
              />
            );
          }
          // Plain string
          if (typeof value === "string") {
            return (
              <GenericStringSection
                key={key}
                fieldKey={key}
                value={value}
                onChange={(v) => set(key, v)}
              />
            );
          }
          return null;
        })}

        {/* Wellbeing Profile chart */}
        <WellbeingProfile
          wellbeingSnapshot={
            summaryForPrint.holisticDashboard?.wellbeingSnapshot
          }
        />

        {/* Suggested Referrals chips */}
        <SuggestedReferrals alliedReferrals={summaryForPrint.alliedReferrals} />

        {/* Full Transcript */}
        {summary?.fullTranscript?.length > 0 && (
          <Accordion
            sx={{
              mb: 2,
              border: "1px solid #e2e8f0",
              boxShadow: "none",
              borderRadius: "8px !important",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ background: "#f8fafc", borderRadius: 2 }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  color: "#0097B2",
                }}
              >
                Full Transcript ({summary.fullTranscript.length} Questions)
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {summary.fullTranscript.map((item, i) => (
                <Box
                  key={i}
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom:
                      i < summary.fullTranscript.length - 1
                        ? "1px solid #f1f5f9"
                        : "none",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#475569",
                      mb: 0.5,
                    }}
                  >
                    Q: {item.q}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#1e293b" }}>
                    A: {item.a}
                  </Typography>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        )}
      </Box>

      {/* ── Footer ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          pt: 2,
          mt: 1,
          borderTop: "1px solid #e2e8f0",
          flexShrink: 0,
        }}
      >
        <Button
          variant="contained"
          disabled={saving}
          onClick={handleSaveAndContinue}
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={{
            ...primaryBtn,
            color: "white",
            background: "linear-gradient(135deg, #0097B2, #007A99)",
          }}
        >
          {saving ? "Saving..." : "Save & Continue"}
        </Button>
      </Box>

      {/* Hidden Print Content */}
      <Box
        sx={{ position: "absolute", top: -9999, left: -9999, width: "210mm" }}
      >
        <PrintContent
          ref={printRef}
          summary={summaryForPrint}
          patientDetails={patientDetails}
          appointmentData={appointmentData}
        />
      </Box>
    </Box>
  );
};

export default PatientHistorySummary;
